import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtMXNFull, fmtDate, todayLocal } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import WorkshopPayModal from "@/components/WorkshopPayModal";
import WorkshopExpensesTab from "@/components/WorkshopExpensesTab";
import WorkshopConceptsTab from "@/components/WorkshopConceptsTab";
import WorkshopMechanicsTab from "@/components/WorkshopMechanicsTab";
import WorkshopHistoryModal from "@/components/WorkshopHistoryModal";
import { Car, Plus, Pencil, Trash2, X, FileText, Check, Ban, Settings, Camera, Wrench, ChevronUp, ChevronDown, DollarSign, FileSpreadsheet, BookOpen, History, Star, Search, List, LayoutGrid, ScanLine, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_CHIP = {
  borrador: { label: "BORRADOR", color: "#94a3b8", bg: "rgba(148,163,184,0.12)" },
  aprobada: { label: "APROBADA", color: "var(--green)", bg: "rgba(34,197,94,0.12)" },
  rechazada: { label: "RECHAZADA", color: "var(--red)", bg: "rgba(220,38,38,0.12)" },
  terminada: { label: "TERMINADA", color: "var(--cyan)", bg: "rgba(34,211,238,0.12)" },
  pagada: { label: "PAGADA", color: "var(--magenta)", bg: "rgba(236,72,153,0.12)" },
};

export default function Workshop() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canWrite = ["admin", "auxiliar", "mecanico"].includes(user?.role);
  const [tab, setTab] = useState("quotes");
  const [vehicles, setVehicles] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [openVehicle, setOpenVehicle] = useState(null); // {} new | vehicle edit
  const [openQuote, setOpenQuote] = useState(null); // {vehicle_id?} new
  const [openSettings, setOpenSettings] = useState(false);
  const [openPay, setOpenPay] = useState(null); // quote
  const [openHistory, setOpenHistory] = useState(null); // vehicle_id
  const [qSearch, setQSearch] = useState("");
  const [vSearch, setVSearch] = useState("");
  const [vView, setVView] = useState("list"); // list | cards
  const [stats, setStats] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    const [v, q, s] = await Promise.all([
      api.get("/workshop/vehicles"),
      api.get(`/workshop/quotes${statusFilter ? `?status=${statusFilter}` : ""}`),
      api.get("/workshop/stats"),
    ]);
    setVehicles(v.data);
    setQuotes(q.data);
    setStats(s.data);
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/workshop/export/excel${statusFilter ? `?status=${statusFilter}` : ""}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `taller_autos_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch { toast.error("Error al exportar"); }
    finally { setExporting(false); }
  };

  const vName = (q) => `${q.vehicle?.brand || ""} ${q.vehicle?.model || ""}`.trim();

  const filteredQuotes = quotes.filter((q) => {
    const t = qSearch.trim().toLowerCase();
    if (!t) return true;
    return [q.folio, q.vehicle?.brand, q.vehicle?.model, q.vehicle?.plates, q.service_description]
      .some((x) => (x || "").toLowerCase().includes(t));
  });

  const filteredVehicles = vehicles.filter((v) => {
    const t = vSearch.trim().toLowerCase();
    if (!t) return true;
    return [v.brand, v.model, v.plates, v.vin, v.custodian, v.client_name, String(v.year || "")]
      .some((x) => (x || "").toLowerCase().includes(t));
  });

  const doAction = async (id, action, body) => {
    try {
      await api.post(`/workshop/quotes/${id}/${action}`, body || {});
      toast.success(action === "approve" ? "Cotización aprobada — el mecánico ya puede trabajar" : action === "reject" ? "Cotización rechazada" : action === "pay" ? "Orden marcada como PAGADA" : "Orden terminada");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const removeQuote = async (id) => {
    if (!window.confirm("¿Eliminar cotización?")) return;
    try { await api.delete(`/workshop/quotes/${id}`); toast.success("Eliminada"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const removeVehicle = async (id) => {
    if (!window.confirm("¿Eliminar vehículo?")) return;
    try { await api.delete(`/workshop/vehicles/${id}`); toast.success("Eliminado"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-4" data-testid="workshop-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="label-mono">// TALLER DE AUTOS</div>
          <h1 className="font-display font-bold text-3xl text-slate-100">Taller</h1>
        </div>
        <div className="flex gap-2">
          <button data-testid="workshop-export-btn" disabled={exporting} onClick={exportExcel} className="btn-ghost px-3 py-2 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5 disabled:opacity-50" style={{ color: "var(--green)" }}>
            <FileSpreadsheet className="w-3.5 h-3.5" /> {exporting ? "Exportando…" : "Excel"}
          </button>
          {user?.role === "admin" && (
            <button data-testid="workshop-settings-btn" onClick={() => setOpenSettings(true)} className="btn-ghost px-3 py-2 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5">
              <Settings className="w-3.5 h-3.5" /> Membrete
            </button>
          )}
          {canWrite && (
            <>
              <button data-testid="new-vehicle-btn" onClick={() => setOpenVehicle({})} className="btn-ghost px-3 py-2 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5">
                <Car className="w-3.5 h-3.5" /> Vehículo
              </button>
              <button data-testid="new-quote-btn" onClick={() => setOpenQuote({})} className="btn-primary px-3 py-2 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center gap-1.5">
                <Plus className="w-3.5 h-3.5" /> Cotización
              </button>
            </>
          )}
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="workshop-stats">
          {[
            ["AUTOS ATENDIDOS", stats.vehicles_atendidos, `${stats.vehicles_total} registrados`, "var(--cyan)"],
            ["COTIZACIONES", stats.quotes_total, `${stats.by_status?.pagada?.count || 0} pagadas`, "var(--text)"],
            ["TOTAL GASTADO", fmtMXNFull(stats.total_gastado || 0), "aprobadas + terminadas + pagadas", "var(--amber)"],
            ["PAGADO", fmtMXNFull(stats.total_pagado || 0), `por pagar ${fmtMXNFull(stats.total_por_pagar || 0)}`, "var(--magenta)"],
          ].map(([label, value, sub, color]) => (
            <div key={label} className="panel p-4">
              <div className="label-mono" style={{ color: "var(--text-mute)" }}>{label}</div>
              <div className="font-display font-bold text-2xl mt-1 tabular-nums" style={{ color }}>{value}</div>
              <div className="text-[10px] mt-0.5" style={{ color: "var(--text-mute)" }}>{sub}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        {[["quotes", "Cotizaciones", FileText], ["vehicles", "Vehículos", Car], ["concepts", "Catálogo", BookOpen], ["mechanics", "Mecánicos", Wrench], ["expenses", "Gastos", DollarSign]].map(([k, label, Icon]) => (
          <button key={k} data-testid={`workshop-tab-${k}`} onClick={() => setTab(k)}
            className={`flex items-center gap-2 px-3 py-2 rounded-sm text-xs uppercase tracking-widest font-semibold ${tab === k ? "" : "hover:bg-white/5"}`}
            style={tab === k ? { background: "var(--cyan)", color: "#0c1424" } : { color: "var(--text-dim)", border: "1px solid var(--border-2)" }}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {tab === "quotes" && (
        <div>
          <div className="flex gap-2 mb-3 flex-wrap items-center">
            <div className="relative min-w-[240px] flex-1 max-w-sm">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5" style={{ color: "var(--text-mute)" }} />
              <input value={qSearch} onChange={(e) => setQSearch(e.target.value)} placeholder="Buscar por marca, placas, folio o servicio…"
                className="w-full input-cyber pl-8 pr-3 py-2 text-sm" data-testid="quotes-search" />
            </div>
            {[["", "Todas"], ["borrador", "Borrador"], ["aprobada", "Aprobadas"], ["terminada", "Terminadas"], ["pagada", "Pagadas"], ["rechazada", "Rechazadas"]].map(([v, l]) => (
              <button key={v} onClick={() => setStatusFilter(v)} className="px-3 py-1 rounded-sm text-[11px] uppercase tracking-widest font-semibold"
                style={statusFilter === v ? { background: "var(--magenta)", color: "white" } : { color: "var(--text-dim)", border: "1px solid var(--border-2)" }}>
                {l}
              </button>
            ))}
          </div>
          <div className="panel overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]" data-testid="workshop-quotes-table">
                <thead className="panel-header">
                  <tr className="text-left">
                    <th className="label-mono px-4 py-3">Folio</th>
                    <th className="label-mono px-4 py-3">Fecha</th>
                    <th className="label-mono px-4 py-3">Vehículo</th>
                    <th className="label-mono px-4 py-3">Servicio</th>
                    <th className="label-mono px-4 py-3 text-right">Total</th>
                    <th className="label-mono px-4 py-3">Estatus</th>
                    <th className="label-mono px-4 py-3">Fotos</th>
                    <th className="label-mono px-4 py-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQuotes.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-slate-500">{qSearch ? "Sin resultados para tu búsqueda." : "Sin cotizaciones. Registra un vehículo y crea la primera."}</td></tr>}
                  {filteredQuotes.map((q) => {
                    const chip = STATUS_CHIP[q.status] || STATUS_CHIP.borrador;
                    return (
                      <tr key={q.id} data-testid={`quote-row-${q.id}`} className="row-hover cursor-pointer" style={{ borderBottom: "1px solid var(--border)" }}
                        onClick={() => navigate(`/workshop/quotes/${q.id}`)}>
                        <td className="px-4 py-3 font-mono text-xs" style={{ color: "var(--cyan)" }}>{q.folio}</td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-300">{fmtDate(q.date)}</td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-slate-100">{vName(q)}</div>
                          <div className="text-[11px] text-slate-500 font-mono">{q.vehicle?.plates}</div>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-300 max-w-[200px] truncate">{q.service_description || "—"}</td>
                        <td className="px-4 py-3 text-right font-mono text-slate-100">
                          {fmtMXNFull(q.total)}
                          {q.public_total != null && q.public_total !== q.total && (
                            <div className="text-[10px]" style={{ color: "var(--green)" }} data-testid={`quote-utility-${q.id}`}>util. {fmtMXNFull(q.utilidad ?? (q.public_total - q.total))}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-sm tracking-widest" style={{ color: chip.color, background: chip.bg, border: `1px solid ${chip.color}` }}>{chip.label}</span>
                          {q.status === "pagada" && q.payment_method && (
                            <div className="text-[10px] mt-1 font-mono" style={{ color: "var(--text-mute)" }}>{q.payment_method}{q.payment_date ? ` · ${fmtDate(q.payment_date)}` : ""}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-[11px] font-mono">
                          <span style={{ color: q.photos_antes > 0 ? "var(--red)" : "var(--text-mute)" }}><Camera className="inline w-3 h-3" /> {q.photos_antes} antes</span>
                          <span className="ml-2" style={{ color: q.photos_despues > 0 ? "var(--green)" : "var(--text-mute)" }}>{q.photos_despues} después</span>
                        </td>
                        <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1 justify-end">
                            {canWrite && ["borrador", "aprobada"].includes(q.status) && (
                              <button data-testid={`edit-quote-${q.id}`} title="Editar cotización" onClick={async () => { const { data } = await api.get(`/workshop/quotes/${q.id}`); setOpenQuote({ quote: data }); }} className="p-1.5 rounded-sm hover:bg-white/5" style={{ color: "var(--cyan)" }}><Pencil className="w-4 h-4" /></button>
                            )}
                            {canWrite && q.status === "borrador" && (
                              <>
                                <button data-testid={`approve-quote-${q.id}`} title="Aprobar" onClick={() => doAction(q.id, "approve")} className="p-1.5 rounded-sm hover:bg-white/5" style={{ color: "var(--green)" }}><Check className="w-4 h-4" /></button>
                                <button data-testid={`reject-quote-${q.id}`} title="Rechazar" onClick={() => { const r = window.prompt("Motivo del rechazo (opcional):"); if (r !== null) doAction(q.id, "reject", { reason: r || null }); }} className="p-1.5 rounded-sm hover:bg-white/5" style={{ color: "var(--red)" }}><Ban className="w-4 h-4" /></button>
                              </>
                            )}
                            {canWrite && q.status === "aprobada" && (
                              <button data-testid={`complete-quote-${q.id}`} title="Marcar terminada" onClick={() => doAction(q.id, "complete")} className="p-1.5 rounded-sm hover:bg-white/5" style={{ color: "var(--cyan)" }}><Wrench className="w-4 h-4" /></button>
                            )}
                            {canWrite && ["aprobada", "terminada"].includes(q.status) && (
                              <button data-testid={`pay-quote-${q.id}`} title="Marcar como pagada" onClick={() => setOpenPay(q)} className="p-1.5 rounded-sm hover:bg-white/5" style={{ color: "var(--magenta)" }}><DollarSign className="w-4 h-4" /></button>
                            )}
                            {user?.role === "admin" && ["borrador", "rechazada"].includes(q.status) && (
                              <button data-testid={`delete-quote-${q.id}`} title="Eliminar" onClick={() => removeQuote(q.id)} className="p-1.5 rounded-sm hover:bg-white/5" style={{ color: "var(--red)" }}><Trash2 className="w-4 h-4" /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {tab === "expenses" && <WorkshopExpensesTab />}

      {tab === "concepts" && <WorkshopConceptsTab canWrite={canWrite} />}

      {tab === "mechanics" && <WorkshopMechanicsTab canWrite={canWrite} />}

      {tab === "vehicles" && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[240px] flex-1 max-w-sm">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5" style={{ color: "var(--text-mute)" }} />
              <input value={vSearch} onChange={(e) => setVSearch(e.target.value)} placeholder="Buscar por marca, placa o serie…"
                className="w-full input-cyber pl-8 pr-3 py-2 text-sm" data-testid="vehicles-search" />
            </div>
            <span className="text-[11px] font-mono" style={{ color: "var(--text-mute)" }}>{filteredVehicles.length} vehículos</span>
            <div className="ml-auto flex gap-1">
              {[["list", List, "Lista"], ["cards", LayoutGrid, "Tarjetas"]].map(([k, Icon, title]) => (
                <button key={k} title={title} data-testid={`vehicles-view-${k}`} onClick={() => setVView(k)}
                  className="p-2 rounded-sm"
                  style={vView === k ? { background: "var(--cyan)", color: "#0c1424" } : { color: "var(--text-dim)", border: "1px solid var(--border-2)" }}>
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div>
          </div>

          {vView === "list" ? (
            <div className="panel overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[900px]" data-testid="vehicles-table">
                  <thead className="panel-header">
                    <tr className="text-left">
                      <th className="label-mono px-4 py-3">Vehículo</th>
                      <th className="label-mono px-4 py-3">Placas</th>
                      <th className="label-mono px-4 py-3">Serie (VIN)</th>
                      <th className="label-mono px-4 py-3 text-right">KM</th>
                      <th className="label-mono px-4 py-3">Cliente</th>
                      <th className="label-mono px-4 py-3">Resguardo</th>
                      <th className="label-mono px-4 py-3 text-center">Cotiz.</th>
                      <th className="label-mono px-4 py-3 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVehicles.length === 0 && <tr><td colSpan={8} className="text-center py-12 text-slate-500">{vSearch ? "Sin resultados para tu búsqueda." : "Sin vehículos registrados."}</td></tr>}
                    {filteredVehicles.map((v) => (
                      <tr key={v.id} data-testid={`vehicle-row-${v.id}`} className="row-hover" style={{ borderBottom: "1px solid var(--border)" }}>
                        <td className="px-4 py-2.5">
                          <div className="font-semibold text-slate-100">{v.brand} {v.model} {v.year || ""}</div>
                          {v.color && <div className="text-[10px] text-slate-500">{v.color}</div>}
                        </td>
                        <td className="px-4 py-2.5"><span className="text-xs font-mono font-bold px-2 py-0.5 rounded-sm" style={{ color: "var(--cyan)", border: "1px solid var(--cyan)" }}>{v.plates}</span></td>
                        <td className="px-4 py-2.5 text-[11px] font-mono text-slate-400">{v.vin || "—"}</td>
                        <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-300">{v.km != null ? Number(v.km).toLocaleString("es-MX") : "—"}</td>
                        <td className="px-4 py-2.5 text-xs" data-testid={`vehicle-client-${v.id}`} style={{ color: v.client_name ? "var(--cyan)" : "var(--text-mute)" }}>{v.client_name || "—"}</td>
                        <td className="px-4 py-2.5 text-xs" style={{ color: v.custodian ? "var(--amber)" : "var(--text-mute)" }}>{v.custodian || "—"}</td>
                        <td className="px-4 py-2.5 text-center font-mono text-xs text-slate-300">{v.quotes_count || 0}</td>
                        <td className="px-4 py-2.5 text-right">
                          <div className="flex items-center gap-1 justify-end">
                            <button data-testid={`history-vehicle-${v.id}`} title="Historial" onClick={() => setOpenHistory(v.id)} className="p-1.5 rounded-sm hover:bg-white/5" style={{ color: "var(--cyan)" }}><History className="w-4 h-4" /></button>
                            {canWrite && (
                              <>
                                <button data-testid={`quote-vehicle-${v.id}`} title="Cotizar" onClick={() => setOpenQuote({ vehicle_id: v.id })} className="p-1.5 rounded-sm hover:bg-white/5" style={{ color: "var(--green)" }}><Plus className="w-4 h-4" /></button>
                                <button data-testid={`edit-vehicle-${v.id}`} title="Editar" onClick={() => setOpenVehicle(v)} className="p-1.5 rounded-sm hover:bg-white/5" style={{ color: "var(--text-dim)" }}><Pencil className="w-4 h-4" /></button>
                                {user?.role === "admin" && (
                                  <button data-testid={`delete-vehicle-${v.id}`} title="Eliminar" onClick={() => removeVehicle(v.id)} className="p-1.5 rounded-sm hover:bg-white/5" style={{ color: "var(--red)" }}><Trash2 className="w-4 h-4" /></button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="workshop-vehicles-grid">
          {filteredVehicles.length === 0 && <div className="col-span-full text-center py-12 text-slate-500 panel">{vSearch ? "Sin resultados para tu búsqueda." : "Sin vehículos registrados."}</div>}
          {filteredVehicles.map((v) => (
            <div key={v.id} data-testid={`vehicle-card-${v.id}`} className="panel p-4 space-y-2">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display font-bold text-slate-100">{v.brand} {v.model} {v.year || ""}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{v.vin || "Sin serie"}</div>
                </div>
                <span className="text-xs font-mono font-bold px-2 py-0.5 rounded-sm" style={{ color: "var(--cyan)", border: "1px solid var(--cyan)" }}>{v.plates}</span>
              </div>
              <div className="text-[11px] text-slate-400 flex gap-3">
                {v.km != null && <span>KM {Number(v.km).toLocaleString("es-MX")}</span>}
                {v.color && <span>{v.color}</span>}
                <span className="ml-auto" style={{ color: "var(--text-mute)" }}>{v.quotes_count || 0} cotizaciones</span>
              </div>
              {v.custodian && <div className="text-[11px]" style={{ color: "var(--amber)" }}>Resguardo: {v.custodian}</div>}
              <div className="flex gap-2 pt-1">
                <button data-testid={`history-vehicle-${v.id}`} onClick={() => setOpenHistory(v.id)} className="btn-ghost flex-1 py-1.5 rounded-sm text-[11px] uppercase tracking-widest font-semibold flex items-center justify-center gap-1.5" style={{ color: "var(--cyan)" }}>
                  <History className="w-3.5 h-3.5" /> Historial
                </button>
                {canWrite && (
                  <>
                    <button data-testid={`quote-vehicle-${v.id}`} onClick={() => setOpenQuote({ vehicle_id: v.id })} className="btn-primary flex-1 py-1.5 rounded-sm text-[11px] uppercase tracking-widest font-semibold">Cotizar</button>
                    <button data-testid={`edit-vehicle-${v.id}`} onClick={() => setOpenVehicle(v)} className="btn-ghost px-2.5 rounded-sm"><Pencil className="w-3.5 h-3.5" /></button>
                    {user?.role === "admin" && (
                      <button data-testid={`delete-vehicle-${v.id}`} onClick={() => removeVehicle(v.id)} className="btn-ghost px-2.5 rounded-sm" style={{ color: "var(--red)" }}><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
          </div>
          )}
        </div>
      )}

      {openVehicle && <VehicleModal vehicle={openVehicle.id ? openVehicle : null} onClose={() => setOpenVehicle(null)} onSaved={() => { setOpenVehicle(null); load(); }} />}
      {openQuote && <QuoteModal vehicles={vehicles} initialVehicleId={openQuote.vehicle_id} quote={openQuote.quote} onClose={() => setOpenQuote(null)} onSaved={(q) => { setOpenQuote(null); load(); if (!openQuote.quote) navigate(`/workshop/quotes/${q.id}`); }} />}
      {openSettings && <SettingsModal onClose={() => setOpenSettings(false)} />}

      {openPay && <WorkshopPayModal quote={openPay} onClose={() => setOpenPay(null)} onPaid={() => { setOpenPay(null); load(); }} />}
      {openHistory && <WorkshopHistoryModal vehicleId={openHistory} onClose={() => setOpenHistory(null)} />}
    </div>
  );
}

const Modal = ({ title, subtitle, onClose, children, wide }) => (
  <div className="fixed inset-0 z-50 p-4 flex items-center justify-center" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
    <div className={`panel-strong w-full ${wide ? "max-w-2xl" : "max-w-md"} overflow-y-auto scan-line`} style={{ maxHeight: "calc(100dvh - 2rem)" }} onClick={(e) => e.stopPropagation()}>
      <div className="panel-header px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div>
          <div className="label-mono">// {subtitle}</div>
          <h2 className="font-display font-bold text-xl text-slate-100 tracking-wide">{title}</h2>
        </div>
        <button onClick={onClose} className="text-slate-500 hover:text-cyan-300"><X className="w-5 h-5" /></button>
      </div>
      {children}
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="label-mono block mb-1.5">{label}</label>
    {children}
  </div>
);

function VehicleModal({ vehicle, onClose, onSaved }) {
  const [f, setF] = useState({
    brand: vehicle?.brand || "", model: vehicle?.model || "", year: vehicle?.year || "",
    plates: vehicle?.plates || "", vin: vehicle?.vin || "", km: vehicle?.km ?? "", color: vehicle?.color || "",
    custodian: vehicle?.custodian || "", notes: vehicle?.notes || "",
    client_id: vehicle?.client_id || "", client_name: "", client_phone: "",
  });
  const [clients, setClients] = useState([]);
  const [newClient, setNewClient] = useState(false);
  useEffect(() => {
    api.get("/workshop/clients").then(({ data }) => setClients(data || [])).catch(() => {});
  }, []);
  const [submitting, setSubmitting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [duplicate, setDuplicate] = useState(null);
  const scanRef = useRef(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const scanCard = async (ev) => {
    const file = Array.from(ev.target.files || [])[0];
    ev.target.value = "";
    if (!file) return;
    setScanning(true);
    setDuplicate(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/workshop/vehicles/scan-card", fd, { timeout: 120000 });
      const d = data.data || {};
      setF((prev) => ({
        ...prev,
        brand: d.brand || prev.brand, model: d.model || prev.model,
        year: d.year || prev.year, plates: d.plates || prev.plates,
        vin: d.vin || prev.vin, color: d.color || prev.color,
      }));
      if (data.existing_vehicle) {
        setDuplicate(data.existing_vehicle);
        toast.error("Este vehículo YA está registrado — no se puede duplicar");
      } else {
        toast.success("Tarjetón leído — revisa los datos y guarda");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo leer el tarjetón"); }
    finally { setScanning(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = { ...f, year: f.year ? Number(f.year) : null, km: f.km !== "" ? Number(f.km) : null, vin: f.vin || null, color: f.color || null, notes: f.notes || null };
      if (newClient) { body.client_id = ""; body.client_name = f.client_name || ""; }
      else { body.client_name = ""; body.client_phone = ""; }
      if (vehicle) await api.patch(`/workshop/vehicles/${vehicle.id}`, body);
      else await api.post("/workshop/vehicles", body);
      toast.success(vehicle ? "Vehículo actualizado" : "Vehículo registrado");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSubmitting(false); }
  };

  return (
    <Modal title={vehicle ? "Editar vehículo" : "Nuevo vehículo"} subtitle="VEHÍCULO" onClose={onClose}>
      <form onSubmit={submit} className="p-6 space-y-3">
        {!vehicle && (
          <>
            <input ref={scanRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={scanCard} data-testid="veh-scan-input" />
            <button type="button" disabled={scanning} onClick={() => scanRef.current?.click()} data-testid="veh-scan-btn"
              className="w-full py-2.5 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ border: "1px dashed var(--cyan)", color: "var(--cyan)", background: "rgba(34,211,238,0.06)" }}>
              {scanning ? <><Loader2 className="w-4 h-4 animate-spin" /> Leyendo tarjetón con IA…</> : <><ScanLine className="w-4 h-4" /> Escanear tarjetón (IA) — llena los datos solo</>}
            </button>
            {duplicate && (
              <div className="px-3 py-2 rounded-sm text-xs" data-testid="veh-duplicate-warning"
                style={{ border: "1px solid var(--red)", background: "rgba(220,38,38,0.12)", color: "#fca5a5" }}>
                ⚠ Este vehículo ya está registrado: <b>{duplicate.brand} {duplicate.model}</b> · placas <b>{duplicate.plates}</b>. No se registrará de nuevo.
              </div>
            )}
          </>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Marca *"><input required value={f.brand} onChange={set("brand")} className="w-full input-cyber px-3 py-2 text-sm" data-testid="veh-brand" placeholder="CHEVROLET" /></Field>
          <Field label="Modelo *"><input required value={f.model} onChange={set("model")} className="w-full input-cyber px-3 py-2 text-sm" data-testid="veh-model" placeholder="SILVERADO 1500" /></Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Año"><input type="number" value={f.year} onChange={set("year")} className="w-full input-cyber px-3 py-2 text-sm" data-testid="veh-year" /></Field>
          <Field label="Placas *"><input required value={f.plates} onChange={set("plates")} className="w-full input-cyber px-3 py-2 text-sm uppercase" data-testid="veh-plates" placeholder="HC4555G" /></Field>
          <Field label="Kilometraje"><input type="number" step="1" value={f.km} onChange={set("km")} className="w-full input-cyber px-3 py-2 text-sm" data-testid="veh-km" /></Field>
        </div>
        <Field label="Número de serie (VIN)"><input value={f.vin} onChange={set("vin")} className="w-full input-cyber px-3 py-2 text-sm uppercase font-mono" data-testid="veh-vin" placeholder="3GCNC9CX5DG317767" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Color"><input value={f.color} onChange={set("color")} className="w-full input-cyber px-3 py-2 text-sm" data-testid="veh-color" /></Field>
          <Field label="Resguardo (responsable)"><input value={f.custodian} onChange={set("custodian")} className="w-full input-cyber px-3 py-2 text-sm" data-testid="veh-custodian" placeholder="ING. JOSUÉ / ARQ. DANIEL" /></Field>
        </div>
        <Field label="Cliente">
          <select
            value={newClient ? "__new__" : (f.client_id || "")}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "__new__") { setNewClient(true); setF({ ...f, client_id: "" }); }
              else { setNewClient(false); setF({ ...f, client_id: v, client_name: "", client_phone: "" }); }
            }}
            className="w-full input-cyber px-3 py-2 text-sm" data-testid="veh-client-select"
          >
            <option value="">— Sin cliente —</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.phone ? ` · ${c.phone}` : ""}</option>)}
            <option value="__new__">+ Nuevo cliente…</option>
          </select>
        </Field>
        {newClient && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nombre del cliente *"><input required value={f.client_name} onChange={set("client_name")} className="w-full input-cyber px-3 py-2 text-sm" data-testid="veh-client-name" placeholder="JUAN PÉREZ" /></Field>
            <Field label="Teléfono"><input value={f.client_phone} onChange={set("client_phone")} className="w-full input-cyber px-3 py-2 text-sm" data-testid="veh-client-phone" /></Field>
          </div>
        )}
        <Field label="Notas"><input value={f.notes} onChange={set("notes")} className="w-full input-cyber px-3 py-2 text-sm" data-testid="veh-notes" /></Field>
        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
          <button type="submit" disabled={submitting || (!vehicle && !!duplicate)} data-testid="veh-save" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">{submitting ? "Guardando…" : "Guardar"}</button>
        </div>
      </form>
    </Modal>
  );
}

function QuoteModal({ vehicles, initialVehicleId, quote, onClose, onSaved }) {
  const [vehicleId, setVehicleId] = useState(quote?.vehicle_id || initialVehicleId || vehicles[0]?.id || "");
  const [date, setDate] = useState(quote?.date || todayLocal());
  const [service, setService] = useState(quote?.service_description || "");
  const [observations, setObservations] = useState(quote?.observations || "");
  const [ivaRate, setIvaRate] = useState(quote?.iva_rate ?? 16);
  const [items, setItems] = useState(
    quote?.items?.length
      ? quote.items.map((it) => ({ concept: it.concept, qty: it.qty, unit: it.unit, price: it.price, public_price: it.public_price ?? "" }))
      : [{ concept: "", qty: 1, unit: "PZA", price: "", public_price: "" }]
  );
  const [submitting, setSubmitting] = useState(false);
  const listRef = useRef(null);
  const [concepts, setConcepts] = useState([]);
  const [suggestFor, setSuggestFor] = useState(null); // índice de fila con sugerencias abiertas

  useEffect(() => {
    api.get("/workshop/concepts").then(({ data }) => setConcepts(data)).catch(() => {});
  }, []);

  const suggestionsFor = (i) => {
    const term = (items[i]?.concept || "").trim().toLowerCase();
    if (!term) return concepts.slice(0, 8);
    return concepts.filter((c) => c.concept.toLowerCase().includes(term)).slice(0, 8);
  };

  const pickConcept = (i, c) => {
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, concept: c.concept, unit: c.unit || "PZA", price: c.price, public_price: c.public_price ?? c.price } : it)));
    setSuggestFor(null);
  };

  const saveToCatalog = async (i) => {
    const it = items[i];
    if (!it.concept.trim() || it.price === "" || Number(it.price) < 0) { toast.error("Escribe el concepto y su precio antes de guardarlo"); return; }
    try {
      const { data } = await api.post("/workshop/concepts", {
        concept: it.concept.trim(), unit: it.unit || "PZA", price: Number(it.price),
        public_price: it.public_price === "" || it.public_price == null ? null : Number(it.public_price),
      });
      toast.success(data.updated ? `"${data.concept}" actualizado en el catálogo` : `"${data.concept}" guardado en el catálogo`);
      const { data: fresh } = await api.get("/workshop/concepts");
      setConcepts(fresh);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar en catálogo"); }
  };

  useEffect(() => {
    if (!vehicleId && vehicles.length > 0) setVehicleId(initialVehicleId || vehicles[0].id);
  }, [vehicles, initialVehicleId, vehicleId]);

  const addItem = () => {
    setItems((prev) => [...prev, { concept: "", qty: 1, unit: "PZA", price: "", public_price: "" }]);
    setTimeout(() => {
      const el = listRef.current;
      if (el) {
        el.scrollTop = el.scrollHeight;
        const inputs = el.querySelectorAll('[data-testid^="quote-item-concept-"]');
        inputs[inputs.length - 1]?.focus();
      }
    }, 50);
  };

  const scrollList = (dir) => {
    const el = listRef.current;
    if (el) el.scrollBy({ top: dir * el.clientHeight * 0.7, behavior: "smooth" });
  };

  const setItem = (i, k, val) => setItems(items.map((it, idx) => (idx === i ? { ...it, [k]: val } : it)));
  const pubOf = (it) => (it.public_price === "" || it.public_price == null ? (Number(it.price) || 0) : Number(it.public_price));
  const subtotal = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.price) || 0), 0);
  const pubSubtotal = items.reduce((s, it) => s + (Number(it.qty) || 0) * pubOf(it), 0);
  const iva = subtotal * (Number(ivaRate) || 0) / 100;
  const pubIva = pubSubtotal * (Number(ivaRate) || 0) / 100;

  const submit = async (e) => {
    e.preventDefault();
    const valid = items.filter((it) => it.concept.trim() && Number(it.price) >= 0 && Number(it.qty) > 0);
    if (valid.length === 0) { toast.error("Agrega al menos un concepto con precio"); return; }
    setSubmitting(true);
    try {
      const body = {
        date, service_description: service || null, observations: observations || null, iva_rate: Number(ivaRate) || 0,
        items: valid.map((it) => ({
          concept: it.concept.trim(), qty: Number(it.qty), unit: it.unit || "PZA", price: Number(it.price),
          public_price: it.public_price === "" || it.public_price == null ? null : Number(it.public_price),
        })),
      };
      if (quote) {
        const { data } = await api.patch(`/workshop/quotes/${quote.id}`, body);
        toast.success(`Cotización ${data.folio} actualizada`);
        onSaved(data);
      } else {
        const { data } = await api.post("/workshop/quotes", { vehicle_id: vehicleId, ...body });
        toast.success(`Cotización ${data.folio} creada`);
        onSaved(data);
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar cotización"); }
    finally { setSubmitting(false); }
  };

  return (
    <Modal title={quote ? `Editar cotización ${quote.folio}` : "Nueva cotización"} subtitle="COTIZACIÓN" onClose={onClose} wide>
      <form onSubmit={submit} className="p-6 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Vehículo *">
            <select required disabled={!!quote} value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} className="w-full input-cyber px-3 py-2 text-sm disabled:opacity-60" data-testid="quote-vehicle">
              {vehicles.length === 0 && <option value="">— Registra un vehículo primero —</option>}
              {vehicles.map((v) => <option key={v.id} value={v.id} style={{ background: "#0a0f1d" }}>{v.brand} {v.model} · {v.plates}</option>)}
            </select>
          </Field>
          <Field label="Fecha"><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full input-cyber px-3 py-2 text-sm" data-testid="quote-date" /></Field>
        </div>
        <Field label="Descripción del servicio"><input value={service} onChange={(e) => setService(e.target.value)} className="w-full input-cyber px-3 py-2 text-sm" placeholder="afinación completa, frenos…" data-testid="quote-service" /></Field>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="label-mono">CONCEPTOS ({items.length})</span>
            <div className="flex items-center gap-2">
              <button type="button" data-testid="quote-scroll-up" onClick={() => scrollList(-1)} title="Subir" className="p-1 rounded-sm hover:bg-white/10" style={{ border: "1px solid var(--border-2)", color: "var(--cyan)" }}><ChevronUp className="w-4 h-4" /></button>
              <button type="button" data-testid="quote-scroll-down" onClick={() => scrollList(1)} title="Bajar" className="p-1 rounded-sm hover:bg-white/10" style={{ border: "1px solid var(--border-2)", color: "var(--cyan)" }}><ChevronDown className="w-4 h-4" /></button>
              <button type="button" data-testid="quote-add-item" onClick={addItem} className="text-xs label-mono hover:underline" style={{ color: "var(--cyan)" }}>+ AGREGAR CONCEPTO</button>
            </div>
          </div>
          <div className="hidden sm:grid gap-2 px-2 mb-1" style={{ gridTemplateColumns: "22px 1fr 58px 78px 92px 92px 26px 28px" }}>
            <span />
            <span className="text-[9px] label-mono" style={{ color: "var(--text-mute)" }}>CONCEPTO</span>
            <span className="text-[9px] label-mono" style={{ color: "var(--text-mute)" }}>CANT</span>
            <span className="text-[9px] label-mono" style={{ color: "var(--text-mute)" }}>UNIDAD</span>
            <span className="text-[9px] label-mono text-right" style={{ color: "var(--cyan)" }}>$ INTERNO</span>
            <span className="text-[9px] label-mono text-right" style={{ color: "var(--amber)" }}>$ PÚBLICO</span>
            <span /><span />
          </div>
          <div ref={listRef} data-testid="quote-items-list" className="space-y-2 overflow-y-auto pr-1.5 py-1 rounded-sm"
            style={{ maxHeight: "34vh", border: "1px solid var(--border)", padding: "8px", scrollbarWidth: "thin", scrollbarColor: "var(--cyan) transparent" }}>
            {items.map((it, i) => (
              <div key={i} className="grid gap-2 items-center" style={{ gridTemplateColumns: "22px 1fr 58px 78px 92px 92px 26px 28px" }}>
                <span className="text-[10px] font-mono text-right" style={{ color: "var(--text-mute)" }}>{i + 1}</span>
                <div className="relative">
                  <input placeholder="Concepto (escribe o elige del catálogo)" value={it.concept}
                    onChange={(e) => { setItem(i, "concept", e.target.value); setSuggestFor(i); }}
                    onFocus={() => setSuggestFor(i)}
                    onBlur={() => setTimeout(() => setSuggestFor((s) => (s === i ? null : s)), 150)}
                    className="w-full input-cyber px-2 py-1.5 text-sm" data-testid={`quote-item-concept-${i}`} autoComplete="off" />
                  {suggestFor === i && concepts.length > 0 && suggestionsFor(i).length > 0 && (
                    <div className="absolute left-0 right-0 top-full mt-1 z-30 rounded-sm overflow-hidden" data-testid={`quote-suggestions-${i}`}
                      style={{ background: "#0c1424", border: "1px solid var(--cyan)", boxShadow: "0 8px 24px rgba(0,0,0,0.6)" }}>
                      {suggestionsFor(i).map((c) => (
                        <button key={c.id} type="button" onMouseDown={(e) => { e.preventDefault(); pickConcept(i, c); }}
                          data-testid={`quote-suggestion-${i}-${c.id}`}
                          className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-white/10 flex items-center justify-between gap-2">
                          <span className="text-slate-200 truncate">{c.concept}</span>
                          <span className="font-mono shrink-0" style={{ color: "var(--cyan)" }}>{fmtMXNFull(c.price)}{c.public_price != null && c.public_price !== c.price ? <span style={{ color: "var(--amber)" }}> → {fmtMXNFull(c.public_price)}</span> : null} <span style={{ color: "var(--text-mute)" }}>/ {c.unit}</span></span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <input type="number" step="0.5" min="0.5" placeholder="Cant" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} className="input-cyber px-2 py-1.5 text-sm" data-testid={`quote-item-qty-${i}`} />
                <select
                  value={it.unit}
                  onChange={(e) => {
                    if (e.target.value === "__custom__") {
                      const u = window.prompt("Escribe la nueva unidad (ej. CUBETA, METRO):");
                      if (u && u.trim()) setItem(i, "unit", u.trim().toUpperCase());
                    } else setItem(i, "unit", e.target.value);
                  }}
                  className="input-cyber px-2 py-1.5 text-sm"
                  data-testid={`quote-item-unit-${i}`}
                >
                  {!["PZA", "SERVICIO", "LITRO", "GARRAFA", "GALON", "JUEGO", "PAR", "LOTE", "KIT"].includes(it.unit) && (
                    <option value={it.unit} style={{ background: "#0a0f1d" }}>{it.unit}</option>
                  )}
                  {["PZA", "SERVICIO", "LITRO", "GARRAFA", "GALON", "JUEGO", "PAR", "LOTE", "KIT"].map((u) => <option key={u} value={u} style={{ background: "#0a0f1d" }}>{u}</option>)}
                  <option value="__custom__" style={{ background: "#0a0f1d", color: "#22d3ee" }}>+ OTRA…</option>
                </select>
                <input type="number" step="0.01" min="0" placeholder="$ interno" value={it.price} onChange={(e) => setItem(i, "price", e.target.value)} className="input-cyber px-2 py-1.5 text-sm text-right" data-testid={`quote-item-price-${i}`} />
                <input type="number" step="0.01" min="0" placeholder="$ público" value={it.public_price} onChange={(e) => setItem(i, "public_price", e.target.value)} className="input-cyber px-2 py-1.5 text-sm text-right" style={{ borderColor: "rgba(245,158,11,0.4)" }} data-testid={`quote-item-public-price-${i}`} />
                <button type="button" title="Guardar en catálogo de conceptos" onClick={() => saveToCatalog(i)} className="text-slate-500 hover:text-amber-400" data-testid={`quote-item-save-concept-${i}`}><Star className="w-4 h-4" /></button>
                <button type="button" onClick={() => setItems(items.filter((_, idx) => idx !== i))} className="text-slate-500 hover:text-red-400" data-testid={`quote-item-remove-${i}`}><X className="w-4 h-4" /></button>
              </div>
            ))}
            <button type="button" data-testid="quote-add-item-bottom" onClick={addItem} className="w-full py-1.5 rounded-sm text-[11px] label-mono hover:bg-white/5" style={{ border: "1px dashed var(--border-2)", color: "var(--cyan)" }}>+ AGREGAR CONCEPTO</button>
          </div>
        </div>

        <div className="flex items-center justify-between px-3 py-2 rounded-sm" style={{ background: "var(--bg-2)", border: "1px solid var(--border-2)" }}>
          <div className="flex items-center gap-2">
            <span className="label-mono">IVA %</span>
            <input type="number" step="0.01" min="0" value={ivaRate} onChange={(e) => setIvaRate(e.target.value)} className="input-cyber px-2 py-1 text-sm w-20" data-testid="quote-iva-rate" />
          </div>
          <div className="text-right text-sm">
            <div className="text-slate-400 text-xs font-mono">Interno: sub {fmtMXNFull(subtotal)} · IVA {fmtMXNFull(iva)}</div>
            <div className="font-display font-bold text-xl" style={{ color: "var(--cyan)" }} data-testid="quote-total-preview">{fmtMXNFull(subtotal + iva)}</div>
            <div className="text-xs font-mono mt-0.5" data-testid="quote-public-preview">
              <span style={{ color: "var(--amber)" }}>Público {fmtMXNFull(pubSubtotal + pubIva)}</span>
              <span className="ml-2" style={{ color: "var(--green)" }}>Utilidad {fmtMXNFull((pubSubtotal + pubIva) - (subtotal + iva))}</span>
            </div>
          </div>
        </div>

        <Field label="Observaciones">
          <textarea rows={2} value={observations} onChange={(e) => setObservations(e.target.value)}
            placeholder="Condiciones, garantía, tiempo de entrega, anticipos…"
            className="w-full input-cyber px-3 py-2 text-sm resize-y" data-testid="quote-observations" />
        </Field>

        <div className="flex gap-2 justify-end pt-3 -mx-6 -mb-6 px-6 pb-6 sticky bottom-0 z-20" style={{ borderTop: "1px solid var(--border)", background: "#0a0f1d" }}>
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
          <button type="submit" disabled={submitting || (!quote && !vehicleId)} data-testid="quote-save" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-40">{submitting ? "Guardando…" : quote ? "Actualizar" : "Crear cotización"}</button>
        </div>
      </form>
    </Modal>
  );
}

function SettingsModal({ onClose }) {
  const [f, setF] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoRef = { current: null };
  useEffect(() => { api.get("/workshop/settings").then(({ data }) => setF({ color: "#1d4ed8", ...data })); }, []);
  if (!f) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const uploadLogo = async (ev) => {
    const file = Array.from(ev.target.files || [])[0];
    ev.target.value = "";
    if (!file) return;
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((prev) => ({ ...prev, logo_file_id: data.id }));
      toast.success("Logo cargado — guarda para aplicar");
    } catch (err) { toast.error(err.response?.data?.detail || "Error al subir logo"); }
    finally { setUploadingLogo(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try { await api.put("/workshop/settings", f); toast.success("Membrete actualizado"); onClose(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSubmitting(false); }
  };
  return (
    <Modal title="Membrete del taller" subtitle="CONFIGURACIÓN" onClose={onClose}>
      <form onSubmit={submit} className="p-6 space-y-3">
        <Field label="Logo del taller">
          <div className="flex items-center gap-3">
            {f.logo_file_id ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.logo_file_id}`} alt="logo" className="h-14 w-14 object-contain rounded-sm bg-white p-1" data-testid="ws-logo-preview" />
            ) : (
              <div className="h-14 w-14 rounded-sm flex items-center justify-center text-[9px] text-slate-500" style={{ border: "1px dashed var(--border-2)" }}>SIN LOGO</div>
            )}
            <input ref={(el) => (logoRef.current = el)} type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="ws-logo-input" />
            <button type="button" disabled={uploadingLogo} onClick={() => logoRef.current?.click()} data-testid="ws-logo-btn" className="btn-ghost px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-widest font-semibold disabled:opacity-50">
              {uploadingLogo ? "Subiendo…" : f.logo_file_id ? "Cambiar logo" : "Subir logo"}
            </button>
            {f.logo_file_id && (
              <button type="button" onClick={() => setF({ ...f, logo_file_id: null })} data-testid="ws-logo-remove" className="text-[11px] label-mono hover:underline" style={{ color: "var(--red)" }}>Quitar</button>
            )}
          </div>
        </Field>
        <Field label="Color del membrete">
          <div className="flex items-center gap-3">
            <input type="color" value={f.color || "#1d4ed8"} onChange={set("color")} className="h-9 w-16 rounded-sm cursor-pointer bg-transparent" data-testid="ws-color" />
            <span className="font-mono text-xs text-slate-400">{f.color || "#1d4ed8"}</span>
            <div className="flex gap-1.5 ml-auto">
              {["#1d4ed8", "#dc2626", "#16a34a", "#0891b2", "#7c3aed", "#ea580c", "#0f172a"].map((c) => (
                <button key={c} type="button" onClick={() => setF({ ...f, color: c })} className="w-6 h-6 rounded-sm" style={{ background: c, border: f.color === c ? "2px solid white" : "1px solid rgba(255,255,255,0.2)" }} title={c} />
              ))}
            </div>
          </div>
        </Field>
        <Field label="Nombre del taller"><input required value={f.name || ""} onChange={set("name")} className="w-full input-cyber px-3 py-2 text-sm" data-testid="ws-name" /></Field>
        <Field label="Lema"><input value={f.slogan || ""} onChange={set("slogan")} className="w-full input-cyber px-3 py-2 text-sm" data-testid="ws-slogan" /></Field>
        <Field label="Firma (ATENTAMENTE)"><input value={f.signature_name || ""} onChange={set("signature_name")} className="w-full input-cyber px-3 py-2 text-sm" data-testid="ws-signature" /></Field>
        <Field label="Dirección"><input value={f.address || ""} onChange={set("address")} className="w-full input-cyber px-3 py-2 text-sm" data-testid="ws-address" /></Field>
        <Field label="Ciudad"><input value={f.city || ""} onChange={set("city")} className="w-full input-cyber px-3 py-2 text-sm" data-testid="ws-city" /></Field>
        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
          <button type="submit" disabled={submitting} data-testid="ws-save" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">Guardar</button>
        </div>
      </form>
    </Modal>
  );
}
